import { formatDate } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { Role } from 'src/app/classes/auth/role.class';
import { CodaltComponent } from 'src/app/codalt.component';
import { AiReportsMessage, AiReportsService } from 'src/app/services/ai-reports.service';

@Component({
    selector: 'app-ai-message',
    standalone: false,
    templateUrl: './ai-message.component.html',
    styleUrl: './ai-message.component.scss',
})
export class AiMessageComponent extends CodaltComponent {
    @Input() message: AiReportsMessage;
    @Input() users = [];
    @Input() roles: Role[] = [];
    @Output() projectClicked = new EventEmitter<string>();
    @Output() loadMoreResults = new EventEmitter<AiReportsMessage>();

    // Sorting properties for tables
    tableSortCache: Map<number, { data: any[]; currentSort: string | null; desc: boolean }> = new Map();

    isQueryVisible = false;

    constructor(private aiReportsService: AiReportsService) {
        super();
    }

    // Method to check if results is a valid array for table display
    isTableData(results: any): boolean {
        return Array.isArray(results) && results.length > 0 && typeof results[0] === 'object';
    }

    // Method to get table headers from the first object in the array
    getTableHeaders(results: any[]): string[] {
        if (!results || results.length === 0) return [];

        const headers = Object.keys(results[0]);

        // Define priority groups - lower index = higher priority
        const priorityGroups = [
            ['medewerker', 'persoon', 'gebruiker', 'materieel', 'naam', 'projectleider', 'uitvoerder'],
            ['soort'],
            ['start', 'begin', 'eerste', 'datum'],
            ['eind', 'stop', 'laatste'],
            ['totaal', 'aantal', 'uren'],
            ['projecten'],
        ];

        // Sort headers according to specified priority order
        return headers
            .sort((a, b) => {
                const aLower = a.toLowerCase();
                const bLower = b.toLowerCase();

                const priorityA =
                    priorityGroups.findIndex(group => group.some(keyword => aLower.includes(keyword))) ??
                    priorityGroups.length;
                const priorityB =
                    priorityGroups.findIndex(group => group.some(keyword => bLower.includes(keyword))) ??
                    priorityGroups.length;

                return priorityA !== priorityB ? priorityA - priorityB : aLower.localeCompare(bLower);
            })
            .filter(h => !['afas_employee_id'].includes(h));
    }

    // Method to get sorted table rows
    getSortedTableRows(message: AiReportsMessage, expanded = false): any[] {
        const messageId = message.id;
        const originalData = message.results || [];

        if (!this.tableSortCache.has(messageId)) {
            // Initialize cache for this message
            this.tableSortCache.set(messageId, {
                data: [...originalData],
                currentSort: null,
                desc: false,
            });
        } else {
            // Update cache data if results have changed (for pagination)
            const cache = this.tableSortCache.get(messageId)!;
            if (cache.data.length !== originalData.length) {
                // Results have changed, update cache with new data while preserving sort
                if (cache.currentSort) {
                    // Re-apply current sort to new data
                    const sortedData = this.applySortToData(originalData, cache.currentSort, cache.desc);
                    cache.data = sortedData;
                } else {
                    // No sort applied, just update data
                    cache.data = [...originalData];
                }
            }
        }

        // Return all available results (pagination is handled at the API level)
        return [...this.tableSortCache.get(messageId)!.data];
    }

    // Method to sort table data
    sortTable(message: AiReportsMessage, column: string): void {
        const messageId = message.id;
        const originalData = message.results || [];

        if (!this.tableSortCache.has(messageId)) {
            this.tableSortCache.set(messageId, {
                data: [...originalData],
                currentSort: null,
                desc: false,
            });
        }

        const cache = this.tableSortCache.get(messageId)!;

        if (cache.currentSort === column) {
            // Same column, reverse order
            cache.data = cache.data.reverse();
            cache.desc = !cache.desc;
        } else {
            // New column, sort ascending
            cache.desc = false;
            cache.data = [...originalData].sort((a, b) => {
                const aVal = a[column];
                const bVal = b[column];

                // Handle null/undefined values
                if (aVal === null || aVal === undefined) return -1;
                if (bVal === null || bVal === undefined) return 1;

                // Check if both values are numeric
                const aNum = Number(aVal);
                const bNum = Number(bVal);
                const bothNumeric = !isNaN(aNum) && !isNaN(bNum);

                if (bothNumeric) {
                    return aNum - bNum;
                } else {
                    // String comparison (case insensitive)
                    const aStr = String(aVal).toLowerCase();
                    const bStr = String(bVal).toLowerCase();
                    return aStr.localeCompare(bStr);
                }
            });
        }

        cache.currentSort = column;
    }

    // Helper method to apply sort to data
    private applySortToData(data: any[], column: string, desc: boolean): any[] {
        const sorted = [...data].sort((a, b) => {
            const aVal = a[column];
            const bVal = b[column];

            // Handle null/undefined values
            if (aVal === null || aVal === undefined) return -1;
            if (bVal === null || bVal === undefined) return 1;

            // Check if both values are numeric
            const aNum = Number(aVal);
            const bNum = Number(bVal);
            const bothNumeric = !isNaN(aNum) && !isNaN(bNum);

            if (bothNumeric) {
                return aNum - bNum;
            } else {
                // String comparison (case insensitive)
                const aStr = String(aVal).toLowerCase();
                const bStr = String(bVal).toLowerCase();
                return aStr.localeCompare(bStr);
            }
        });

        return desc ? sorted.reverse() : sorted;
    }

    // Method to get current sort state for a table
    getTableSortState(message: AiReportsMessage): { currentSort: string | null; desc: boolean } {
        const messageId = message.id;
        const cache = this.tableSortCache.get(messageId);

        if (!cache) {
            return { currentSort: null, desc: false };
        }

        return { currentSort: cache.currentSort, desc: cache.desc };
    }

    // Method to toggle SQL query visibility for a specific message
    toggleQueryVisibility(messageId: number): void {
        this.isQueryVisible = !this.isQueryVisible;
    }

    // Method to handle load more results request
    onLoadMoreResults(): void {
        this.loadMoreResults.emit(this.message);
    }

    // Method to export message table data to Excel
    async exportMessageToExcel(message: AiReportsMessage) {
        message.loadingExcel = true;
        this.subscriptions.add(
            this.aiReportsService.getMessageResults(message.id, 9999999).subscribe({
                next: async results => {
                    if (!this.isTableData(results.data.results)) {
                        return;
                    }

                    const workbook = new ExcelJS.Workbook();
                    const worksheet = workbook.addWorksheet('AI Report Data');

                    // Get table data
                    const tableData = results.data.results as any[];
                    const headers = this.getTableHeaders(tableData);

                    // Add headers
                    const headerRow = worksheet.addRow(headers);
                    worksheet.views = [{ state: 'frozen', ySplit: 1 }]; // Freeze the first row
                    headerRow.eachCell(cell => {
                        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: 'FF4472C4' },
                        };
                        cell.alignment = { horizontal: 'center', vertical: 'middle' };
                        cell.border = {
                            top: { style: 'thin' },
                            left: { style: 'thin' },
                            bottom: { style: 'thin' },
                            right: { style: 'thin' },
                        };
                    });

                    // Add data rows
                    tableData.forEach(row => {
                        const dataRow = worksheet.addRow(
                            headers.map(header => {
                                const value = row[header];

                                // Check if the value is numeric and convert it to a number for Excel
                                if (value !== null && value !== undefined && value !== '') {
                                    const numValue = Number(value);
                                    if (!isNaN(numValue) && isFinite(numValue)) {
                                        return numValue;
                                    }
                                }

                                return value;
                            })
                        );

                        // Apply formatting and borders
                        dataRow.eachCell((cell, colNumber) => {
                            cell.border = {
                                top: { style: 'thin' },
                                left: { style: 'thin' },
                                bottom: { style: 'thin' },
                                right: { style: 'thin' },
                            };

                            // Apply number formatting for numeric cells
                            if (typeof cell.value === 'number') {
                                // Check if it's a whole number or decimal
                                if (cell.value % 1 === 0) {
                                    // Whole number format
                                    cell.numFmt = '#,##0';
                                } else {
                                    // Decimal number format (up to 2 decimal places)
                                    cell.numFmt = '#,##0.00';
                                }
                                cell.alignment = { horizontal: 'right', vertical: 'middle' };
                            } else {
                                cell.alignment = { horizontal: 'left', vertical: 'middle' };
                            }
                        });
                    });

                    // Auto-fit columns
                    worksheet.columns.forEach(column => {
                        let maxLength = 0;
                        column.eachCell({ includeEmpty: true }, cell => {
                            const columnLength = cell.value ? cell.value.toString().length : 10;
                            if (columnLength > maxLength) {
                                maxLength = columnLength;
                            }
                        });
                        column.width = maxLength < 30 ? maxLength + 2 : 30;
                    });

                    // Generate filename with timestamp in Dutch time
                    const now = new Date();
                    const timestamp = formatDate(now, 'dd-MM-yyyy-HH-mm-ss', 'nl-NL');
                    const filename = `${message['chat_name']}-${timestamp}.xlsx`;

                    setTimeout(async () => {
                        // Export file
                        const buffer = await workbook.xlsx.writeBuffer();
                        const blob = new Blob([buffer], {
                            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                        });
                        saveAs(blob, filename);
                        message.loadingExcel = false;
                    });
                },
            })
        );
    }

    checkIfIncludesHours(string): boolean {
        return /hour|hours|uur|uren/i.test(string);
    }

    // Check if a value looks like a project number
    isProjectNumber(value: any, header: string): boolean {
        if (!value) return false;
        const stringValue = String(value);

        // Check if header suggests it's a project-related field
        const isProjectHeader = /project|projectnr|projectnummer|projektnummer|projektnr/i.test(header);

        return isProjectHeader;
    }

    // Check if a value matches datetime format (YYYY-MM-DD HH:MM:SS)
    isDateTimeFormat(value: any): boolean {
        if (!value) return false;
        const stringValue = String(value);
        return /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(stringValue);
    }
}
